import {useMemo} from 'react'

import {useQuery} from '@apollo/client'

import {
  CompanyBenefitsGuideLanguagesDocument,
  CompanyBenefitsGuideLanguagesQuery,
  CompanyBenefitsGuidePdfDocument,
  Country,
  ProductLine,
} from '@progyny/generated/cms-graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useCMSClient} from '@progyny/store/cmsClientStore'
import {useMeStore} from '@progyny/store/meStore'

/**
 * Custom hook to manage benefits guide queries
 * Provides access to both language query and PDF query with proper variable handling
 */
export const useBenefitsGuide = (language?: string) => {
  const user = useMeStore((s) => s.user)
  const {productLine} = useProductLine()
  const cmsClient = useCMSClient()

  // Get country code from user
  const countryCode: Country | null = useMemo(() => {
    return user?.member?.countryOfEmployment?.country?.code
      ? (user.member.countryOfEmployment.country.code as Country)
      : null
  }, [user?.member?.countryOfEmployment?.country?.code])

  // Get company ID
  const companyId = useMemo(() => {
    return user?.company?.id as string | undefined
  }, [user?.company?.id])

  // Convert productLine string to ProductLine enum type
  const productLineEnum: ProductLine | undefined = useMemo(() => {
    return productLine ? (productLine as ProductLine) : undefined
  }, [productLine])

  const shouldSkipLanguages =
    !companyId || productLine !== ProductLine.Fertility
  // Query for available languages using CMS client
  const {
    data: languagesData,
    loading: languagesLoading,
    error: languagesError,
  } = useQuery(CompanyBenefitsGuideLanguagesDocument, {
    client: cmsClient, // Use CMS client instead of default client
    variables: {
      companyId: companyId!,
      country: countryCode,
    },
    skip: shouldSkipLanguages, // Skip if no company ID
  })

  // Query for PDF based on language and productLine using CMS client
  // Only fetch PDF if language is explicitly provided (skip on home page where we only need languages)
  const {
    data: pdfData,
    loading: pdfLoading,
    error: pdfError,
    refetch: refetchPdf,
  } = useQuery(CompanyBenefitsGuidePdfDocument, {
    client: cmsClient, // Use CMS client instead of default client
    variables: {
      companyId: companyId!,
      country: countryCode,
      languageCode: language || 'en', // GraphQL query expects languageCode, not language
      productLine: productLineEnum,
    },
    skip: !companyId || !language, // Skip if no company ID or no language provided
  })

  // Extract available languages from query result
  // Returns array of objects with both languageCode and languageName
  const availableLanguages = useMemo(() => {
    if (!languagesData?.companyResources) return []

    // Create a map to ensure uniqueness by languageCode
    const languageMap = new Map<
      string,
      {languageCode: string; languageName: string}
    >()

    languagesData.companyResources.forEach(
      (
        resource: CompanyBenefitsGuideLanguagesQuery['companyResources'][number]
      ) => {
        if (resource?.languageCode && resource?.languageName) {
          // Use languageCode as the key to ensure uniqueness
          if (!languageMap.has(resource.languageCode)) {
            languageMap.set(resource.languageCode, {
              languageCode: resource.languageCode,
              languageName: resource.languageName,
            })
          }
        }
      }
    )

    return Array.from(languageMap.values())
  }, [languagesData])

  // Get PDF URL from query result
  const pdfUrl = useMemo(() => {
    return pdfData?.companyResources?.[0]?.file?.url || null
  }, [pdfData])

  return {
    // Language query
    availableLanguages,
    languagesLoading,
    languagesError,
    // PDF query
    pdfUrl,
    pdfLoading,
    pdfError,
    refetchPdf,
    // Helper data
    companyId,
    countryCode,
    productLine: productLineEnum,
  }
}
